import { useEffect, useId, useRef, useState } from 'react';

const WIDTH = 208;

/**
 * The "..." menu of a table row (WAI-ARIA menu button pattern). The popup is
 * position: fixed so the table's scroll container cannot clip it; it closes
 * on scroll and resize rather than chase the button.
 * Every item stays focusable, a disabled one says why through aria-describedby.
 */
export default function RowActionsMenu({ name, actions }) {
  const items = actions.filter((a) => !a.hidden);
  const [open, setOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(0);
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const itemRefs = useRef([]);
  const baseId = useId();

  useEffect(() => {
    if (open) itemRefs.current[focusIndex]?.focus();
  }, [open, focusIndex]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!menuRef.current?.contains(e.target) && !buttonRef.current?.contains(e.target)) setOpen(false);
    };
    const close = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  if (items.length === 0) return null;

  function place() {
    const r = buttonRef.current.getBoundingClientRect();
    const height = items.length * 40 + 8;
    const left = Math.max(8, Math.min(window.innerWidth - WIDTH - 8, r.right - WIDTH));
    const above = r.bottom + 4 + height > window.innerHeight && r.top > height;
    setPos(above ? { left, bottom: window.innerHeight - r.top + 4 } : { left, top: r.bottom + 4 });
  }

  function openMenu(at) {
    const firstEnabled = Math.max(0, items.findIndex((a) => !a.disabled));
    setFocusIndex(at === 'last' ? items.length - 1 : firstEnabled);
    place();
    setOpen(true);
  }

  function close(returnFocus) {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }

  function onButtonKeyDown(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); openMenu('first'); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); openMenu('last'); }
  }

  function onMenuKeyDown(e) {
    const last = items.length - 1;
    if (e.key === 'ArrowDown') { e.preventDefault(); setFocusIndex((i) => (i >= last ? 0 : i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setFocusIndex((i) => (i <= 0 ? last : i - 1)); }
    else if (e.key === 'Home') { e.preventDefault(); setFocusIndex(0); }
    else if (e.key === 'End') { e.preventDefault(); setFocusIndex(last); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(true); }
    else if (e.key === 'Tab') close(false);
  }

  function choose(action) {
    if (action.disabled) return;
    close(true);
    action.onSelect();
  }

  return (
    <span className="inline-block">
      <button
        ref={buttonRef}
        type="button"
        aria-label={`Actions for ${name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? close(false) : openMenu('first'))}
        onKeyDown={onButtonKeyDown}
        className="tap inline-flex items-center justify-center rounded p-1.5 text-slate-600 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ipc-light"
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor"><circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" /></svg>
      </button>
      {open ? (
        <div
          ref={menuRef}
          role="menu"
          aria-label={`Actions for ${name}`}
          onKeyDown={onMenuKeyDown}
          style={{ position: 'fixed', width: WIDTH, ...pos }}
          className="z-30 rounded-md border border-ipc-border bg-white py-1 text-left shadow-lg"
        >
          {items.map((a, i) => {
            const reasonId = a.disabled && a.disabledReason ? `${baseId}-${a.id}-why` : undefined;
            return (
              <div key={a.id}>
                <button
                  ref={(el) => { itemRefs.current[i] = el; }}
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  aria-disabled={a.disabled ? 'true' : undefined}
                  aria-describedby={reasonId}
                  onClick={() => choose(a)}
                  className={`block w-full px-3 py-2 text-left text-sm focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ipc-light focus-visible:bg-slate-100 ${a.disabled ? 'cursor-not-allowed text-slate-500' : (a.danger ? 'text-red-700 hover:bg-red-50' : 'text-slate-800 hover:bg-slate-50')}`}
                >
                  {a.label}
                </button>
                {reasonId ? <span id={reasonId} className="block px-3 pb-1 text-xs text-slate-600">{a.disabledReason}</span> : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </span>
  );
}
